//interface Options {
//
//}

/**
 * Lightbox.
 *
 * @author Felix Kiesewetter <info@felixkiesewetter.de>
 * @lastModified 2026-06-17 Bugfix on loading images.
 * @package fk_js/lightbox
 * @version 2.6.1
 */
class Lightbox
{

    /* private */ zoomLevel /* :number */ = 1;

    /**
     * Class constructor
     *
     * @param {object} options
     * @returns {void}
     */
    constructor(options={})
    {
        this.settings = this.setSettings(options);
        this.overlay = document.querySelector(`.${this.settings.lightboxClassName}`);
        this.container = document.querySelector(`.${this.settings.lightboxClassName}__container`);
        this.closeButton = document.querySelector(`.${this.settings.lightboxClassName}__dismiss`);
        this.controls = document.querySelectorAll(`.${this.settings.lightboxClassName}__set-control`);
        this.titleBox = document.querySelectorAll(`.${this.settings.lightboxClassName}__title`);
        this.elements = document.querySelectorAll(this.settings.elementSelector);
        this.triggers = document.querySelectorAll(this.settings.triggerSelector);
        this.gdprConsentInternal = false;
        this.styles = {};
        this.sets = this.getSets();
        this.lastClickedElement = false;
        this.config = {
            // Content params
            content: false, // div or iframe
            contentType: false, // youtube or url or image
            contentHeight: false, // if set the height will be fixed
            contentWidth: false, // if set the width will be fixed
            contentMaxWidth: this.settings.maxContentWidth, // if set the width will never exceed config value
            // Lightbox params
            src: false, // the src that is being shown within the config
            // Youtube params
            videoID: false, // ID of the youtube video
            videoQuality: false, // Quality parameter of the youtube video
            videoWindow: false
        };
        this.gdpr = {
            container: {},
            button: {},
            confirmed: false
        };
        this.init();
    }

    /**
     * Initialise the lightbox
     *
     * Creates DOM elements
     * Appends event listeners
     *
     * @returns {void}
     */
    init()
    {
        if (!this.overlay || !this.container) {
            this.createElements();
        }
        for (const element of this.elements) {
            element.addEventListener('click', (event) => this.show(event, element));
        }
        for (const trigger of this.triggers) {
            trigger.addEventListener('click', (event) => {
                event.preventDefault();
                const setId = trigger.href.split(':')[1];
                if (typeof this.sets[setId] === 'object') {
                    if (typeof this.sets[setId][0] === 'object') {
                        this.sets[setId][0].dispatchEvent(new Event('click'));
                    }
                }
            });
        }
    }

    /**
     * Create Elements
     *
     * Creates DOM elements required for the lightbox if not already available
     *
     * @returns {void}
     */
    createElements()
    {
        if (!this.overlay) {
            this.settings.debug && console.log('[Lightbox]', 'createElements()', 'Creating Lightbox Overlay')
            this.overlay = document.createElement('div')
            this.overlay.classList.add(this.settings.lightboxClassName)
            document.body.appendChild(this.overlay)
        }
        if (!this.container) {
            this.settings.debug && console.log('[Lightbox]', 'createElements()', 'Creating Lightbox Container')
            this.container = document.createElement('div')
            this.container.classList.add(`${this.settings.lightboxClassName}__container`)
            this.overlay.appendChild(this.container)
        }
        if (this.settings.close) {
            if (!this.closeButton) {
                this.closeButton = document.createElement('a');
                this.closeButton.classList.add(`${this.settings.lightboxClassName}__dismiss`);
                this.closeButton.innerHTML = '&times;';
                this.closeButton.addEventListener('click', () => { this.hide(); });
                this.overlay.appendChild(this.closeButton);
            }
        }
        if (this.settings.zoom) {
            if (!this.zoomButton) {
                this.zoomButton = document.createElement('a');
                this.zoomButton.classList.add(`${this.settings.lightboxClassName}__zoom`);
                this.zoomButton.innerHTML = '&#x1F50E;&#xFE0E;';
                this.zoomButton.addEventListener('click', () => { this.zoom(); });
                this.overlay.appendChild(this.zoomButton);
            }
        }
        if (this.settings.title) {
            this.titleBox = document.createElement('p')
            this.titleBox.classList.add(`${this.settings.lightboxClassName}__title`)
            this.overlay.appendChild(this.titleBox)
        }
        if (!this.controls.length) {
            let controlPrev = document.createElement('a')
            controlPrev.classList.add(`${this.settings.lightboxClassName}__set-control`)
            controlPrev.classList.add(`${this.settings.lightboxClassName}__set-control--prev`)
            controlPrev.innerHTML = '&#8592;'
            this.overlay.appendChild(controlPrev)
            let controlNext = document.createElement('a')
            controlNext.classList.add(`${this.settings.lightboxClassName}__set-control`)
            controlNext.classList.add(`${this.settings.lightboxClassName}__set-control--next`)
            controlNext.innerHTML = '&#8594;'
            this.overlay.appendChild(controlNext)
            this.controls = this.overlay.querySelectorAll(`.${this.settings.lightboxClassName}__set-control`)
            for (const control of this.controls) {
                control.addEventListener('click', (e) => {
                    e.stopPropagation()
                    this.controlHandler(control)
                })
            }
        }
    }

    /**
     * Zoom into the lightbox content.
     */
    zoom(zoomLevel /* :number|null */ = null) /* :Lightbox */
    {
        if (zoomLevel) {
            this.zoomLevel = zoomLevel;
        }
        else {
            this.zoomLevel += .5;
            if (2 < this.zoomLevel) {
                this.zoomLevel = 1;
            }
        }
        this.config.content.style.setProperty('--zoom-level', this.zoomLevel);
        return this;
    }

    hide()
    {
        this.zoom(1);
        if (this.config.contentType === 'youtube') {
            // TODO: stop YouTube Video on closing the Lightbox
            console.log(this.content);
            //config.videoWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}','*');
        }
        this.overlay.classList.remove(`${this.settings.lightboxClassName}--visible`);
        setTimeout(() => {
            this.overlay.style.display = 'none';
            this.settings.hideCallback(this);
        }, this.settings.fadeDuration);
    }

    show(event, element)
    {
        event.preventDefault();
        if (this.lastClickedElement !== element) {
            this.lastClickedElement = element;
            this.setConfig(element);
            this.setTitle(this.settings.titleCallback(element));
        }
        else if ('url' === this.config.contentType && this.settings.refreshFrame) {
            this.config.content.src = this.config.content.src;
        }
        this.overlay.style.display = 'block';
        setTimeout(() => {
            this.overlay.classList.add(`${this.settings.lightboxClassName}--visible`);
            this.settings.showCallback(this);
        }, 100);
        return false;
    }

    showControls ()
    {
        for (const control of this.controls) {
            control.style.display = 'block';
        }
    }

    hideControls()
    {
        for (const control of this.controls) {
            control.style.display = 'none';
        }
    }

    /**
     * Event Handler For Controls
     *
     * @param control
     */
    controlHandler(control)
    {
        let index;
        if (control.classList.contains(`${this.settings.lightboxClassName}__set-control--next`)) {
            index = this.config.set.index + 1;
        }
        else {
            index = this.config.set.index - 1;
        }
        index = (index < 0 ? this.config.set.elements.length - 1 : (index >= this.config.set.elements.length ? 0 : index));
        this.config.set.elements[index].click();
    }

    /**
     * Define video quality for embedded YouTube videos
     *
     * @returns {string}
     */
    defineVideoQuality()
    {
        let videoQuality = this.settings.defaultVideoQuality;
        if (this.settings.defaultVideoQuality === 'auto') {
            if (this.config.contentWidth <= 480) {
                videoQuality = 'small';
            }
            else if (this.config.contentWidth <= 640) {
                videoQuality = 'medium';
            }
            else if (this.config.contentWidth <= 720) {
                videoQuality = 'large';
            }
            else if (this.config.contentWidth <= 1280) {
                videoQuality = 'hd720';
            }
            else if (this.config.contentWidth > 1280) {
                videoQuality = 'hd1080';
            }
        }
        this.settings.debug && console.log('[Lightbox]', 'defineVideoQuality()', videoQuality);
        return videoQuality;
    }

    /**
     * Get video id of YouTube video
     *
     * @param href
     * @returns {string}
     */
    getVideoId(href)
    {
        let videoId;
        if (href.match(/v=/)) {
            videoId = href.split('?v=')[1];
        }
        else if (href.match(/embed\//)) {
            videoId = href.split('embed/')[1];
        }
        else if (href.match(/youtu.be/)) {
            videoId = href.split('.be/')[1];
        }
        this.settings.debug && console.log('[Lightbox]', 'getVideoId()', videoId);
        return videoId;
    }

    /**
     * Get Sets
     * Iterates through elements to get lightbox sets
     * @returns {void}
     */
    getSets()
    {
        let sets = {};
        for (const element of this.elements) {
            const set = this.getSetFromElement(element);
            if (null !== set) {
                if (typeof sets[set] === 'undefined') {
                    sets[set] = [];
                }
                sets[set].push(element);
            }
        }
        return sets;
    }

    /**
     * Get Set From Element
     * @param {object} element
     * @returns {string | null}
     */
    getSetFromElement(element={})
    {
        return element.getAttribute('data-lightbox') || element.getAttribute('data-lightbox-set') || null
    }

    /**
     * Set Title
     * @param {string} title
     */
    setTitle(title='')
    {
        if (this.settings.title) {
            this.titleBox.innerHTML = title || '';
        }
    }

    /**
     * Set Configuration
     * Sets the current lightbox configuration object
     * @param {object} element
     */
    setConfig(element={})
    {
        const setId = this.getSetFromElement(element);
        const elementsInSet = this.sets[setId] || [];
        if (setId && 1 < elementsInSet.length) {
            this.config.set = {
                id: setId,
                elements: elementsInSet,
                index: Array.prototype.slice.call(elementsInSet).indexOf(element)
            };
            this.showControls();
        }
        else {
            this.config.set = false;
            this.hideControls();
        }
        this.config.contentMaxWidth = this.settings.maxContentWidth;
        this.config.contentType = this.getContentType(element);
        let data = [];
        this.config.src = data['src'] || element.href;
        this.config.contentMaxWidth = parseInt(data['max-width'] || this.settings.maxContentWidth);
        this.config.contentWidth = data['width'] || (this.config.contentType !== 'image' ? this.settings.defaultFrameWidth : false);
        this.config.contentHeight = data['height'] || (this.config.contentType === 'url' ? this.settings.defaultFrameHeight : false);
        this.config.videoQuality = data['quality'] || (this.config.contentType === 'youtube' ? this.defineVideoQuality() : false);
        this.config.videoID = (this.config.contentType == 'youtube' ? this.getVideoId(element.href) : false);
        this.config.content = this.setContent();
        this.settings.debug && console.log('[Lightbox]', 'Lightbox Configuration', this.config);
    }

    /**
     * Set Content
     * Based on the current configuration and desired content, returns the lightbox's HTML content
     * @returns {HTMLImageElement|HTMLIFrameElement} content
     */
    setContent()
    {
        if (this.config.content) {
            this.container.innerHTML = '';
        }
        if (this.config.contentType !== 'image') {
            if (typeof this.config.contentWidth === 'string' && this.config.contentWidth.indexOf('%') >= 1) {
                this.config.contentWidth = (this.overlay.width()/100)*parseInt(this.config.contentWidth);
            }
            this.config.contentHeight = this.config.contentHeight || this.config.contentWidth/this.settings.defaultFrameRatio;
            this.styles = {
                'width': this.config.contentWidth,
                'height': this.config.contentHeight
            };
        }
        let content;
        switch (this.config.contentType) {

            // Display an image
            case 'image':
                content = document.createElement('img');
                content.src = this.config.src;
                content.classList.add(`${this.settings.lightboxClassName}__image`);
                content.style.width = this.config.contentWidth;
                content.style.maxWidth = this.config.contentMaxWidth;
                content.onload = () => {
                    content.setAttribute('data-orientation', content.width <= content.height ? 'portrait' : 'landscape');
                };
                break;

            // Display a video
            case 'video':
                content = document.createElement('video');
                const source = document.createElement('source');
                content.classList.add(`${this.settings.lightboxClassName}__video`);
                content.style.width = this.config.contentWidth;
                content.style.maxWidth = this.config.contentMaxWidth;
                source.src = this.config.src;
                content.appendChild(source);
                if (this.settings.videoControls) content.controls = true;
                if (this.settings.videoMute) content.muted = true;
                if (this.settings.videoAutoplay) content.autoplay = true;
                break;

            // Display an iframe
            case 'url':
                content = document.createElement('iframe');
                content.classList.add(`${this.settings.lightboxClassName}__iframe`);
                content.setAttribute('allowfullscreen', false);
                content.src = this.config.src;
                content.setAttribute('scrolling', this.settings.scrolling ? 'yes' : 'no');
                break;

            // Embed a YouTube video
            case 'youtube':

                // Check for consent
                if (this.settings.gdpr && !this.settings.gdprCallback()) {
                    content = document.createElement('div');
                    content.classList.add(`${this.settings.lightboxClassName}__gdpr`);
                    content.innerHTML = this.settings.gdprTemplate;
                    if (content.querySelector('button')) {
                        content.querySelector('button').addEventListener('click', () => {
                            this.gdprConsentInternal = true;
                            // TODO: this does not change anything at the moment ...
                            // TODO: store in local storage, reload, etc.
                            // TODO: or dont store anything and use a function like content = this.createYouTubeIFrame() ...
                        });
                    }
                }
                else {
                    let params = `?rel=${this.settings.youtubeRelated}`;
                    params+=`&modestbranding=${this.settings.youtubeModestBranding}`;
                    params+=`&showsearch=${this.settings.youtubeShowSearch}`;
                    params+=`&autoplay=${this.settings.youtubeAutoplay}`;
                    params+=`&showinfo=${this.settings.youtubeShowInfo}`;
                    params+=`&vq=${this.config.videoQuality}`;
                    params+=`&enablejsapi=1`;
                    const youtubeEmbedUri = `https://www.youtube${this.settings.youtubeNoCookie ? '-nocookie' : ''}.com/embed/${this.config.videoID}${params}`;
                    content = document.createElement('iframe');
                    content.classList.add(`${this.settings.lightboxClassName}__iframe`);
                    content.classList.add(`${this.settings.lightboxClassName}__iframe--youtube`);
                    content.setAttribute('allowfullscreen', this.settings.youtubeAllowFullscreen);
                    content.src = youtubeEmbedUri;
                }
                break;
        }
        if (this.settings.zoom) {
            content.classList.add('-zoomable');
            content.addEventListener('mousewheel', (e) => {
                const direction = e.deltaY > 0 ? 'DOWN' : 'UP';
                if ('UP' === direction && 2 !== this.zoomLevel) {
                    this.zoom();
                }
                else if ('DOWN' === direction) {
                    this.zoom(1);
                }
            });
            content.addEventListener('click', (e) => {
                this.zoom();
            });
        }
        this.container.appendChild(content);
        setTimeout(() => {
            content.classList.add('-visible');
        }, 50);
        return content;
    }

    /**
     * Get content type
     *
     * @param {HTMLElement} element
     * @returns {string}
     */
    getContentType(element)
    {
        const href = element.href.toLowerCase();
        const imgtest = /(\.gif|\.jpg|\.png)+$/;
        const videotest = /(\.mp4|\.m4v|\.mov|\.webm)+$/;
        const tubetest = /(\youtube)/;
        if (imgtest.test(href)) {
            return 'image';
        }
        else if (videotest.test(href)) {
            return 'video';
        }
        else if (tubetest.test(href)) {
            return 'youtube';
        }
        return 'url';
    }

    /**
     * Set settings
     *
     * @param {object} options
     * @returns {object}
     */
    setSettings(options)
    {
        const defaultSettings = {
            zoom: true,
            // @param {bool} allow scrolling
            scrolling: false,
            // @param {bool} debug mode
            debug: false,
            // @param {bool} use close button or not
            close: true,
            // @param {bool} use title or not
            title: true,
            // @param {function} the titleCallback by default fetches the clicked elements title attribute but this can be fully customised
            titleCallback: (element) => {
                return element.getAttribute('title') || false;
            },
            // @param {bool} use autoplay for content type 'video'
            videoAutoplay: true,
            // @param {bool} use controls for content type 'video'
            videoControls: true,
            // @param {bool} use muted attribute for content type 'video'
            videoMute: false,
            defaultVideoQuality: 'auto',
            defaultFrameWidth: 1024,
            defaultFrameRatio: 1.78,
            fadeDuration: 1000,
            lightboxClassName: 'lightbox',
            maxContentWidth: 1920,
            elementSelector: '[data-lightbox],[href*="youtube"],[href$=".jpg"],[href$=".JPG"],[href$=".png"],[href$=".PNG"],[href$=".gif"],[href$=".GIF"],[href$=".mp4"],[href$=".MP4"],[href$=".m4v"],[href$=".M4V"],[href$=".mov"],[href$=".MOV"],[href$=".webm"],[href$=".WEBM"]',
            triggerSelector: '[href*="lightbox:"]',
            refreshFrame: true,
            youtubeRelated: 0,
            youtubeModestBranding: 0,
            youtubeShowSearch: 0,
            youtubeAutoplaye: 1,
            youtubeShowInfo: 0,
            youtubeControls: 1,
            youtubeNoCookie: 1,
            youtubeAllowFullscreen: 1,
            showCallback: () => {

            },
            hideCallback: () => {

            },
            // @param {bool} check for consent in accordance with GDPR
            gdpr: true,
            // @param {function} callback function to check for consent status
            gdprCallback: () => {
                return this.gdprConsentInternal;
            },
            // @param {string} gdpr container template if no consent is given
            gdprTemplate: `<p>Mit dem Starten des Videos erklären Sie sich mit <a href="/datenschutz" target="_blank">unseren Datenschutzvereinbarungen</a> sowie den <a href="https://www.google.de/intl/de/policies/privacy" target="_blank">Datenschutzrichtlinien von Google</a> einverstanden. <button>Einverstanden</button></p>`
        };
        return {...defaultSettings, ...options};
    }

}

export default Lightbox;
